/**
 * Utilidades para identificar tipos de preguntas en encuestas
 */

/**
 * Tipos de preguntas soportados
 */
export const QUESTION_TYPES = {
  // Selección
  RADIO: ['selección', 'escala likert', 'escala likert -eng'],
  RADIO_FACE: 'sentimental',
  CHECKBOX: ['opción múltiple', 'opcion multiple con imagenes'],

  // Texto
  TEXT: 'texto',
  INFORMATIVE_TEXT: 'texto informativo',

  // Desplegables y relacionales
  SELECT: 'select',
  RELATIONAL: 'relacional',

  // Escalas
  RANGE: 'calificaciones',
  ENPS: 'E-NPS',
  OPINION_SCALE: 'escala de opinión',
  BIPOLAR_SCALE: 'escala bipolar',
  CONSTANT_ADD: 'suma constante',

  // Fechas
  DATE: 'fecha',

  // Especiales
  MATRIX: 'matriz',
  BIG_SELECTION: 'selección gran escala',
  COMPLEX: 'compuesta',
  MULTIMEDIA: 'multimedia',
  FILE: 'archivos',
  TABLE: 'tabla',
};

/**
 * Normaliza el tipo de pregunta a minúsculas
 * @private
 */
const normalize = (typeQuestion) => {
  return typeQuestion?.toLowerCase() || '';
};

/**
 * Verifica si un tipo coincide con un valor o array de valores
 * @private
 */
const matchesType = (typeQuestion, expected) => {
  const normalized = normalize(typeQuestion);
  if (Array.isArray(expected)) {
    return expected.includes(normalized);
  }
  return normalized === expected;
};

/**
 * Verifica si la pregunta es de tipo radio (selección simple)
 * Incluye: "selección", "escala likert", "escala likert -eng"
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isRadio = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.RADIO);
};

/**
 * Verifica si la pregunta es de tipo radio con caras (sentimental)
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isRadioFace = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.RADIO_FACE);
};

/**
 * Verifica si la pregunta es de tipo checkbox (opción múltiple)
 * Incluye: "opción múltiple", "opcion multiple con imagenes"
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isCheckbox = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.CHECKBOX);
};

/**
 * Verifica si la pregunta es de tipo rango (calificaciones)
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isRange = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.RANGE);
};

/**
 * Verifica si la pregunta es de tipo suma constante
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isConstantAdd = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.CONSTANT_ADD);
};

/**
 * Verifica si la pregunta es de tipo texto
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isText = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.TEXT);
};

/**
 * Verifica si la pregunta es de tipo texto informativo
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isInformativeText = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.INFORMATIVE_TEXT);
};

/**
 * Verifica si la pregunta es de tipo select (desplegable)
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isSelect = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.SELECT);
};

/**
 * Verifica si la pregunta es de tipo relacional
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isRelational = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.RELATIONAL);
};

/**
 * Verifica si la pregunta es de tipo E-NPS (slider)
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isSlider = (typeQuestion) => {
  return typeQuestion === QUESTION_TYPES.ENPS; // Case-sensitive
};

/**
 * Verifica si la pregunta es de tipo escala de opinión (E-NPS Promoter)
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isENPSPromoter = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.OPINION_SCALE);
};

/**
 * Verifica si la pregunta es de tipo escala bipolar
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isBipolarSlider = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.BIPOLAR_SCALE);
};

/**
 * Verifica si la pregunta es de tipo fecha
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isDate = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.DATE);
};

/**
 * Verifica si la pregunta es de tipo matriz
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isMatrix = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.MATRIX);
};

/**
 * Verifica si la pregunta es de tipo selección gran escala
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isBigSelection = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.BIG_SELECTION);
};

/**
 * Verifica si la pregunta es de tipo compuesta
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isComplex = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.COMPLEX);
};

/**
 * Verifica si la pregunta es de tipo multimedia
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isMultimedia = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.MULTIMEDIA);
};

/**
 * Verifica si la pregunta es de tipo archivos
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isFile = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.FILE);
};

/**
 * Verifica si la pregunta es de tipo tabla
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isTable = (typeQuestion) => {
  return matchesType(typeQuestion, QUESTION_TYPES.TABLE);
};

/**
 * Obtiene el tipo de pregunta normalizado
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {string|null} Tipo normalizado o null si no es válido
 */
export const getQuestionType = (typeQuestion) => {
  const normalized = normalize(typeQuestion);

  // Buscar en todos los tipos definidos
  for (const [key, value] of Object.entries(QUESTION_TYPES)) {
    if (Array.isArray(value) && value.includes(normalized)) {
      return key;
    }
    if (value === normalized) {
      return key;
    }
  }

  return null;
};

/**
 * Verifica si un tipo de pregunta es válido
 * @param {string} typeQuestion - Tipo de pregunta
 * @returns {boolean}
 */
export const isValidQuestionType = (typeQuestion) => {
  return getQuestionType(typeQuestion) !== null;
};
